<script setup lang="ts">
import { generateEnergyBillEstimation } from '@/services/eos/billing-estimates.service'
import type { LiteSite } from '@/types/core/Site'
import type { IEnergyBillEstimationGenerationFilters } from '@/types/eos/filters'
import type { ICreateEnergyBillEstimationForm } from '@/types/eos/forms'
import { downloadFile } from '@/util'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import { DateTime } from 'luxon'
import { reactive, ref } from 'vue'

const props = defineProps<{
  sites: LiteSite[]
  defaultSite?: string
}>()

const submitLoading = ref<boolean>(false)

const model = defineModel<boolean>()
const createBillingEstimationFormRef = ref<FormInstance>()
const createBillingEstimationForm = reactive<IEnergyBillEstimationGenerationFilters>({
  site: '',
  period: DateTime.now().toJSDate(),
  response_format: 'pdf',
})

const rules = reactive<FormRules<ICreateEnergyBillEstimationForm>>({
  site: [{ required: true, message: 'Site is required.' }],
  period: [{ required: true, message: 'Period is required.' }],
})

/**
 * Handles form submit.
 *
 * @param formEl form element ref.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  formEl?.validate(async (valid, fields) => {
    if (valid) {
      try {
        submitLoading.value = true
        const pdfBlob = (await generateEnergyBillEstimation(createBillingEstimationForm)) as Blob
        downloadFile(
          pdfBlob,
          `Bill Estimate of ${createBillingEstimationForm.site} ${DateTime.fromJSDate(createBillingEstimationForm.period).toFormat('MMMM yyyy')}.pdf`
        )
        resetFormCloseDialog()
      } catch (err: any) {
        ElMessage({
          type: 'error',
          message: `Operation failed: ${err.message}`,
        })
      } finally {
        submitLoading.value = false
      }
    }
  })
}

const initForm = async () => {
  createBillingEstimationForm.site = props.defaultSite ?? ''
}

/**
 * Resets form, closes dialog.
 */
const resetFormCloseDialog = () => {
  resetForm(createBillingEstimationFormRef.value)
  model.value = false
}

/**
 * Resets the form.
 *
 * @param formEl form element ref.
 */
const resetForm = (formEl: FormInstance | undefined) => {
  formEl?.resetFields()
}
</script>

<template>
  <el-dialog v-model="model" title="Generate Bill Estimate PDF" :show-close="false" :width="350" @open="initForm">
    <el-form
      ref="createBillingEstimationFormRef"
      :model="createBillingEstimationForm"
      :rules="rules"
      label-width="auto"
      label-position="left"
    >
      <el-form-item label="Site" prop="site">
        <el-select v-model="createBillingEstimationForm.site" placeholder="Site" clearable>
          <el-option v-for="s in props.sites" :key="s.site_name" :label="s.site_name" :value="s.site_id" />
        </el-select>
      </el-form-item>
      <el-form-item label="Period" prop="period">
        <el-date-picker
          v-model="createBillingEstimationForm.period"
          type="month"
          placeholder="Select month"
          format="MMMM YYYY"
        ></el-date-picker>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="resetFormCloseDialog">Cancel</el-button>
      <el-button type="primary" :loading="submitLoading" @click="submitForm(createBillingEstimationFormRef)">
        Generate PDF
      </el-button>
    </template>
  </el-dialog>
</template>
